import { assign, createMachine, InterpreterFrom, StateConfig, StateFrom } from 'xstate'

import { OnboardingData, OnboardingStudentUserMetadata } from '../types'

export type OnboardStudentContext = OnboardingData &
  OnboardingStudentUserMetadata & { customerId?: string }

export type OnboardStudentEvent =
  | { type: 'CONTINUE' }
  | { type: 'BACK' }
  | { type: 'RESTORE' }
  | { type: 'UPDATE_FIELDS'; data: Partial<OnboardStudentContext> }

export type OnboardStudentState = {
  value: 'initial' | 'createAccount' | 'enrollment' | 'address' | 'invalid' | 'complete'
  context: OnboardStudentContext
  changed?: boolean
  done?: boolean
}

export type OnboardStudentMachine = typeof machine
export type OnboardStudentInterpreter = InterpreterFrom<OnboardStudentMachine>
export type OnboardStudentObserverCallback = (state: OnboardStudentStateObject) => void
export type OnboardStudentStateConfig = StateConfig<OnboardStudentContext, OnboardStudentEvent>
export type OnboardStudentStateObject = StateFrom<OnboardStudentMachine>

export type OnboardStudentStateValue = OnboardStudentState['value']

export const machine = createMachine<
  OnboardStudentContext,
  OnboardStudentEvent,
  OnboardStudentState
>({
  predictableActionArguments: true,
  id: 'onboard_student',
  initial: 'initial',
  context: {
    firstName: '',
    lastName: '',
    phone: '',
    graduationDate: '',
    universityName: '',
    specialty: '',
    certificateUrl: '',
    address: {
      street: [],
      city: '',
      state: '',
      zip: '',
      countryCode: '',
    },
  },
  states: {
    initial: {
      on: {
        '*': [
          {
            cond: ({ customerId }) => Boolean(customerId),
            target: 'complete',
          },
          {
            cond: ({ customerId }) => !customerId,
            target: 'createAccount',
          },
        ],
      },
    },
    createAccount: {
      on: {
        CONTINUE: 'enrollment',
      },
    },
    enrollment: {
      on: {
        CONTINUE: 'address',
        BACK: 'createAccount',
      },
    },
    address: {
      on: {
        CONTINUE: 'complete',
        BACK: 'enrollment',
      },
    },
    invalid: {
      on: {
        RESTORE: 'complete',
      },
    },
    complete: {
      entry: 'submit',
      type: 'final',
    },
  },
  on: {
    UPDATE_FIELDS: {
      actions: assign((context, event) => ({
        ...context,
        ...(event.type === 'UPDATE_FIELDS' ? event.data : {}),
      })),
    },
  },
})

export const createOnboardStudentMachine = () => machine
